import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { mensagemDeErro } from '@core/errors';
import { HttpErrorResponse } from '@angular/common/http';
import { TokenService } from '@modules/auth/services/token.service';
import { MeuConvite } from '@modules/usuarios/model/convite';
import { ConvitesService } from '@modules/usuarios/services/convites.service';
import { DialogConfirmacaoComponent } from '@shared/components/dialog-confirmacao/dialog-confirmacao.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { NotificacaoToasterService } from '@shared/services/notificacao-toaster.service';
import { TenantService } from '@shared/services/tenant.service';
import { AppButtonDirective } from '@shared/ui/button/app-button.directive';
import { AppInputDirective } from '@shared/ui/input/input.directive';
import { AppLabelDirective } from '@shared/ui/label/label.directive';
import { ModalComponent } from '@shared/ui/modal/modal.component';
import { ModalFooterComponent } from '@shared/ui/modal/components/modal-footer.component';
import { ModalHeaderComponent } from '@shared/ui/modal/components/modal-header.component';

import { avisoDeConvitesJaApareceu, marcarAvisoDeConvitesApareceu } from '../marca-aviso-convites';

/**
 * Em que ponto está cada convite: decidindo, digitando o código, já aceito, ou sem valer mais
 * (cancelado, vencido ou decidido em outro lugar depois que o aviso abriu).
 */
type EtapaConvite = 'decidir' | 'codigo' | 'aceito' | 'invalido';

/** Estado de um cartão do aviso. Cada convite anda sozinho: aceitar um não mexe nos outros. */
interface CartaoConvite {
  convite: MeuConvite;
  etapa: EtapaConvite;
  codigo: string;
  enviando: boolean;
  erro: string | null;
  /** Empresa liberada pelo aceite, para o "Trocar para a empresa". */
  tenantId: string | null;
  nomeEmpresaAceita: string | null;
}

const TAMANHO_CODIGO = 6;

/**
 * Aviso de convites depois do login (#825). Ao montar o layout, busca os convites abertos para o
 * e-mail da conta e, se houver, abre um modal com um cartão por convite.
 *
 * Aceitar exige o código de 6 dígitos que o sistema manda para o e-mail: o e-mail da conta é
 * digitado pelo admin e ninguém confirma que a pessoa é dona dele. Recusar não pede código, mas
 * pede confirmação. Fechar sem decidir não grava nada.
 *
 * O aviso abre uma vez por login: F5 e a troca de empresa não o abrem de novo (ver
 * `marca-aviso-convites.ts`). No próximo login ele volta.
 */
@Component({
  selector: 'app-aviso-convites',
  imports: [
    DatePipe,
    ModalComponent,
    ModalHeaderComponent,
    ModalFooterComponent,
    AppButtonDirective,
    AppInputDirective,
    AppLabelDirective,
    IconComponent,
    DialogConfirmacaoComponent,
  ],
  templateUrl: './aviso-convites.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvisoConvitesComponent implements OnInit {
  private readonly _convites = inject(ConvitesService);
  private readonly _tenantService = inject(TenantService);
  private readonly _toast = inject(NotificacaoToasterService);
  private readonly _tokenService = inject(TokenService);
  private readonly _destroyRef = inject(DestroyRef);

  protected readonly aberto = signal(false);
  protected readonly cartoes = signal<CartaoConvite[]>([]);
  protected readonly trocando = signal(false);
  protected readonly tamanhoCodigo = TAMANHO_CODIGO;
  /** Convite cuja recusa espera confirmação. */
  protected readonly recusaPendente = signal<MeuConvite | null>(null);

  ngOnInit(): void {
    const usuarioId = this._tokenService.getTokenPayload()?.sub ?? null;
    if (usuarioId && avisoDeConvitesJaApareceu(usuarioId)) return;

    this._convites
      .meusConvites()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (convites) => {
          if (!convites.length) return;
          this.cartoes.set(convites.map((convite) => this.novoCartao(convite)));
          this.aberto.set(true);
          if (usuarioId) marcarAvisoDeConvitesApareceu(usuarioId);
        },
        error: () => {
          // O aviso não é crítico: se a consulta falhar, não atrapalha o uso do sistema.
        },
      });
  }

  protected aceitar(id: string): void {
    this.pedirCodigo(id, false);
  }

  protected reenviarCodigo(id: string): void {
    this.pedirCodigo(id, true);
  }

  protected digitarCodigo(id: string, evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const codigo = input.value.replace(/\D/g, '').slice(0, TAMANHO_CODIGO);
    // Devolve ao campo só os dígitos, para o que se vê ser o que vai.
    input.value = codigo;
    this.atualizar(id, { codigo, erro: null });
  }

  protected codigoCompleto(cartao: CartaoConvite): boolean {
    return cartao.codigo.length === TAMANHO_CODIGO;
  }

  protected confirmar(id: string): void {
    const cartao = this.cartao(id);
    if (!cartao || !this.codigoCompleto(cartao) || cartao.enviando) return;

    this.atualizar(id, { enviando: true, erro: null });
    this._convites.aceitarMeu(id, cartao.codigo).subscribe({
      next: ({ tenantId, nomeEmpresa }) =>
        this.atualizar(id, { etapa: 'aceito', enviando: false, tenantId, nomeEmpresaAceita: nomeEmpresa }),
      error: (err) => this.falhou(id, err, 'Não foi possível aceitar o convite.'),
    });
  }

  /** Recusar pede confirmação antes, como na página do link. */
  protected pedirRecusa(id: string): void {
    const cartao = this.cartao(id);
    if (cartao && !cartao.enviando) this.recusaPendente.set(cartao.convite);
  }

  protected desistirRecusa(): void {
    this.recusaPendente.set(null);
  }

  protected confirmarRecusa(): void {
    const convite = this.recusaPendente();
    this.recusaPendente.set(null);
    if (convite) this.recusar(convite.id);
  }

  private recusar(id: string): void {
    this.atualizar(id, { enviando: true, erro: null });
    this._convites.recusarMeu(id).subscribe({
      next: () => this.remover(id),
      error: (err) => this.falhou(id, err, 'Não foi possível recusar o convite.'),
    });
  }

  /** Troca a sessão para a empresa que o aceite liberou e recarrega, como no seletor de empresas. */
  protected trocarParaEmpresa(id: string): void {
    const tenantId = this.cartao(id)?.tenantId;
    if (!tenantId || this.trocando()) return;

    this.trocando.set(true);
    this._tenantService
      .switchTenant(tenantId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this.aberto.set(false);
          setTimeout(() => this.recarregarPagina(), 300);
        },
        error: (err) => {
          this.trocando.set(false);
          this.atualizar(id, { erro: mensagemDeErro(err, 'Não foi possível trocar de empresa.') });
        },
      });
  }

  /** Fecha sem gravar nada: o convite continua pendente e o aviso volta no próximo login. */
  protected fechar(): void {
    this.aberto.set(false);
  }

  protected recarregarPagina(): void {
    window.location.reload();
  }

  private pedirCodigo(id: string, reenvio: boolean): void {
    this.atualizar(id, { enviando: true, erro: null });
    this._convites.pedirCodigo(id).subscribe({
      next: () => {
        // O código antigo deixa de valer com o novo: o campo volta vazio.
        this.atualizar(id, { etapa: 'codigo', enviando: false, codigo: '' });
        if (reenvio) this._toast.sucesso('Enviamos um novo código para o seu e-mail.');
      },
      // 429: o servidor diz quanto esperar ("Aguarde um minuto para reenviar."). 502: o e-mail não
      // saiu ("Não foi possível enviar o e-mail agora. Tente reenviar.").
      error: (err) =>
        this.falhou(id, err, 'Não foi possível enviar o código.', {
          manterMensagemDo429: true,
          manterMensagemDo502: true,
        }),
    });
  }

  /**
   * Erro de uma ação do cartão. 404 é o convite que deixou de existir depois que o aviso abriu
   * (cancelado, vencido): o cartão passa a dizer isso e perde as ações. O resto fica no cartão.
   */
  private falhou(id: string, err: unknown, fallback: string, opcoes?: Parameters<typeof mensagemDeErro>[2]): void {
    if (err instanceof HttpErrorResponse && err.status === 404) {
      this.atualizar(id, { etapa: 'invalido', enviando: false, erro: null, codigo: '' });
      return;
    }
    this.atualizar(id, { enviando: false, erro: mensagemDeErro(err, fallback, opcoes) });
  }

  private novoCartao(convite: MeuConvite): CartaoConvite {
    return {
      convite,
      etapa: 'decidir',
      codigo: '',
      enviando: false,
      erro: null,
      tenantId: null,
      nomeEmpresaAceita: null,
    };
  }

  private cartao(id: string): CartaoConvite | undefined {
    return this.cartoes().find((c) => c.convite.id === id);
  }

  private atualizar(id: string, mudanca: Partial<CartaoConvite>): void {
    this.cartoes.update((lista) => lista.map((c) => (c.convite.id === id ? { ...c, ...mudanca } : c)));
  }

  private remover(id: string): void {
    this.cartoes.update((lista) => lista.filter((c) => c.convite.id !== id));
    if (!this.cartoes().length) this.aberto.set(false);
  }
}
